import { useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatContextType, type AgentFilter, type CustomFilter } from '@trinity/chat';
import {
  ActiveChatSessionContext,
  ChatContextItemsContext,
} from '@trinity/chat-components/components/Chat/context';
import { FilterTypeEnum } from '@trinity/chat-components/components/Chat.Filters/interfaces';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { AgentResourcesContext, type AgentResourcesContextValue } from './context';
import { useSyncTerritoryFilterFromSubject } from './hooks/useSyncTerritoryFilterFromSubject';

type Props = {
  children: ReactNode;
};

type TerritoryResourcesContainerProps = {
  agentIdentifier: string | null;
  brandIdentifier: string;
  enabled: boolean;
  isAgentContext: boolean;
  filters: AgentFilter[];
  children: (data: {
    territoryOptions: CustomFilter.ItemValue[];
    defaultTerritoryOption: CustomFilter.ItemValue | null;
    hasLoadedTerritories: boolean;
  }) => ReactNode;
};

function TerritoryResourcesContainer({
  agentIdentifier,
  brandIdentifier,
  enabled,
  isAgentContext,
  filters,
  children,
}: TerritoryResourcesContainerProps) {
  const apiClient = useChatApiClient();

  const territoryFilter = useMemo(() => {
    return filters.find((filter) => filter.type === FilterTypeEnum.UserTerritory);
  }, [filters]);

  const hasUserTerritoryFilter = territoryFilter !== undefined;

  const userTerritoriesQuery = useQuery({
    queryKey: ['user-territories', agentIdentifier, brandIdentifier, apiClient.agentResources],
    queryFn: () => apiClient.agentResources.getUserTerritories({ brandIdentifier }),
    enabled: enabled && hasUserTerritoryFilter,
    initialData: { territories: [] },
  });

  const territoryOptions = useMemo(() => {
    return userTerritoriesQuery.data.territories.map((territory) => ({
      id: territory.territoryKey,
      name: territory.name,
    }));
  }, [userTerritoriesQuery.data.territories]);

  const defaultTerritoryOption = useMemo(() => {
    const defaultTerritory = userTerritoriesQuery.data.territories.find((territory) => territory.isDefault);
    if (!defaultTerritory) {
      return null;
    }

    return {
      id: defaultTerritory.territoryKey,
      name: defaultTerritory.name,
    };
  }, [userTerritoriesQuery.data.territories]);

  const hasLoadedTerritories = useMemo(() => {
    if (!hasUserTerritoryFilter) {
      return false;
    }

    return !userTerritoriesQuery.isLoading;
  }, [hasUserTerritoryFilter, userTerritoriesQuery.isLoading]);

  useSyncTerritoryFilterFromSubject({
    enabled,
    isAgentContext,
    filters,
    territoryOptions,
    defaultTerritoryOption,
    hasLoadedTerritories,
  });

  return <>{children({ territoryOptions, defaultTerritoryOption, hasLoadedTerritories })}</>;
}

export function AgentResourcesProvider({ children }: Props) {
  const session = useContext(ActiveChatSessionContext);
  const contextItems = useContext(ChatContextItemsContext);
  const apiClient = useChatApiClient();
  const brandIdentifier = useActiveBrandIdentifierNullSafe();

  // The chat context's own `identifier` is not the agent identifier — it
  // must be resolved by looking up the matching ChatContextItem.
  const agentIdentifier = useMemo(() => {
    if (session.context.type !== ChatContextType.Agent) return null;
    const chatContextIdentifier = session.context.data.identifier;
    return contextItems.find((item) => item.identifier === chatContextIdentifier)?.agent.identifier ?? null;
  }, [session.context.data, session.context.type, contextItems]);

  const agentResourcesEnabled = useMemo(() => {
    return agentIdentifier !== null;
  }, [agentIdentifier]);

  const agentResourcesQuery = useQuery({
    queryKey: ['agent-resources', agentIdentifier, apiClient.agentResources],
    queryFn: () => {
      if (!agentIdentifier) {
        return Promise.resolve({ dataSources: [], filters: [] });
      }
      return apiClient.agentResources.getAgentResources({ agentIdentifier });
    },
    enabled: agentResourcesEnabled,
    initialData: { dataSources: [], filters: [] },
  });

  return (
    <TerritoryResourcesContainer
      agentIdentifier={agentIdentifier}
      brandIdentifier={brandIdentifier}
      enabled={agentResourcesEnabled}
      isAgentContext={session.context.type === ChatContextType.Agent}
      filters={agentResourcesQuery.data.filters}
    >
      {({ territoryOptions, defaultTerritoryOption, hasLoadedTerritories }) => {
        const contextValue: AgentResourcesContextValue = {
          data: agentResourcesQuery.data,
          isLoading: agentResourcesQuery.isLoading,
          isError: agentResourcesQuery.isError,
          error: agentResourcesQuery.error,
          territoryOptions,
          defaultTerritoryOption,
          hasLoadedTerritories,
          agentIdentifier,
        };

        return (
          <AgentResourcesContext.Provider value={contextValue}>{children}</AgentResourcesContext.Provider>
        );
      }}
    </TerritoryResourcesContainer>
  );
}

export function useAgentResources(): AgentResourcesContextValue {
  const context = useContext(AgentResourcesContext);

  if (!context) {
    throw new Error('useAgentResources must be used within an AgentResourcesProvider');
  }

  return context;
}
